import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import { convertPrice } from '@/utils/common';
import { formatDate } from '@/utils/date';
import _ from 'lodash';
import moment from 'moment';

const DebtRepository = RepositoryFactory.get('debts');
const InstallmentRepository = RepositoryFactory.get('installments');
const StoreRepository = RepositoryFactory.get('stores');

/**
 * DebtInstallmentBillService - Business logic layer for debt installment bill operations
 * Handles data transformation, calculations, and API orchestration
 */
export default {
  /**
   * Get debt installment bill transactions with filtering and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.pageSize - Items per page
   * @param {string} params.fromDate - Start date filter
   * @param {string} params.toDate - End date filter
   * @param {number} params.storeId - Store ID filter
   * @param {number} params.contactId - Contact/Installment ID filter
   * @param {string} params.contractCode - Contract code filter
   * @param {string} params.docId - Document ID filter
   * @param {string} params.customerMobile - Customer mobile filter
   * @param {string} params.isFullPaid - Payment status filter
   * @returns {Promise<Object>} Formatted debt installment bill data
   */
  async getInstallmentBillTransactions(params = {}) {
    try {
      const response = await DebtRepository.getInstallmentBillTransactions(params);
      return this.formatInstallmentBillData(response);
    } catch (error) {
      console.error('Error in DebtInstallmentBillService.getInstallmentBillTransactions:', error);
      throw error;
    }
  },

  /**
   * Get installments for autocomplete dropdown
   * @returns {Promise<Array>} Formatted installment options
   */
  async getInstallmentsForAutocomplete() {
    try {
      const installments = await InstallmentRepository.getForAutocomplete();
      return [
        { value: null, text: 'Tất cả dịch vụ trả góp' },
        ...installments.map(item => ({
          value: item.id,
          text: item.name,
        })),
      ];
    } catch (error) {
      console.error('Error in DebtInstallmentBillService.getInstallmentsForAutocomplete:', error);
      throw error;
    }
  },

  /**
   * Get stores for autocomplete
   * @returns {Promise<Array>} Formatted store options
   */
  async getStoresForAutocomplete() {
    try {
      const stores = await StoreRepository.getForAutocomplete();
      return stores;
    } catch (error) {
      console.error('Error in DebtInstallmentBillService.getStoresForAutocomplete:', error);
      throw error;
    }
  },

  /**
   * Search stores by text
   * @param {string} searchText - Search text
   * @param {number} limit - Maximum results
   * @returns {Promise<Array>} Filtered store options
   */
  async searchStores(searchText = '', limit = 10) {
    try {
      const results = await StoreRepository.search(searchText, limit);
      return results;
    } catch (error) {
      console.error('Error in DebtInstallmentBillService.searchStores:', error);
      throw error;
    }
  },

  /**
   * Load initial data concurrently
   * @returns {Promise<Object>} Combined initial data
   */
  async loadInitialData() {
    try {
      const [installments, stores] = await Promise.all([
        this.getInstallmentsForAutocomplete(),
        this.getStoresForAutocomplete(),
      ]);

      return {
        installments,
        stores,
      };
    } catch (error) {
      console.error('Error in DebtInstallmentBillService.loadInitialData:', error);
      throw error;
    }
  },

  /**
   * Format installment bill data for display
   * @param {Object} rawData - Raw API response data
   * @returns {Object} Formatted data
   */
  formatInstallmentBillData(rawData) {
    return {
      dataset: rawData.dataset || [],
      total: rawData.total || 0,
      pages: rawData.pages || 1,
    };
  },

  /**
   * Calculate total amounts for summary
   * @param {Array} dataset - Bill dataset
   * @param {string} field - Field to sum
   * @returns {string} Formatted total amount
   */
  calculateTotalAmount(dataset, field) {
    const total = _.sumBy(dataset, (bill) => bill[field] || 0);
    return convertPrice(total);
  },

  /**
   * Calculate all summary totals
   * @param {Array} dataset - Bill dataset
   * @returns {Object} All calculated totals
   */
  calculateSummaryTotals(dataset) {
    return {
      totalPaymentAmount: this.calculateTotalAmount(dataset, 'sumAmountPayments'),
      totalPaidAmount: this.calculateTotalAmount(dataset, 'sumAmountTransactions'),
      totalRemainingAmount: this.calculateTotalAmount(dataset, 'sumRemainTransaction'),
      totalBillAmount: this.calculateTotalAmount(dataset, 'totalAmount'),
    };
  },

  /**
   * Check if row should be shown (for rowspan logic)
   * @param {Array} payments - Payment array
   * @param {Object} currentItem - Current payment item
   * @returns {boolean} Whether to show the row
   */
  isShowRow(payments, currentItem) {
    return _.findIndex(payments, ['id', currentItem.id]) === 0;
  },

  /**
   * Generate note for payment
   * @param {Object} bill - Bill data
   * @param {Object} item - Payment item
   * @returns {string} Generated note
   */
  generatePaymentNote(bill, item) {
    let note = `Nhận thanh toán từ dịch vụ trả góp\n`;
    note += `Mã HĐ: ${bill.billNumber}\n`;
    note += `Dịch vụ: ${item.paymentName}\n`;
    note += `Mã hợp đồng: ${item.paymentCode || ''}`;
    return note;
  },

  /**
   * Build API parameters from filters
   * @param {Object} filters - Filter object
   * @param {Object} route - Route object
   * @returns {Object} API parameters
   */
  buildApiParameters(filters, route) {
    const page = route.query.page || 1;
    
    return {
      page: parseInt(page),
      pageSize: 10,
      storeId: filters.storeId || null,
      contactId: filters.contactId || null,
      fromDate: filters.fromDate 
        ? moment(filters.fromDate, 'DD/MM/YYYY').format('DD/MM/YYYY')
        : null,
      toDate: filters.toDate 
        ? moment(filters.toDate, 'DD/MM/YYYY').format('DD/MM/YYYY')
        : null,
      contractCode: filters.contractCode || null,
      docId: filters.docId || null,
      customerMobile: filters.customerMobile || null,
      customerName: filters.customerName || null,
      isFullPaid: filters.isFullPaid || null,
    };
  },

  /**
   * Validate filter inputs
   * @param {Object} filters - Filter object
   * @returns {Object} Validation result
   */
  validateFilters(filters) {
    const errors = [];
    
    // Add validation logic as needed
    if (filters.fromDate && filters.toDate) {
      const fromDate = moment(filters.fromDate, 'DD/MM/YYYY');
      const toDate = moment(filters.toDate, 'DD/MM/YYYY');
      
      if (fromDate.isAfter(toDate)) {
        errors.push('Ngày bắt đầu không thể sau ngày kết thúc');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Format date for display
   * @param {string} dateString - Date string
   * @returns {string} Formatted date
   */
  formatDisplayDate(dateString) {
    return formatDate(dateString);
  },

  /**
   * Format currency for display
   * @param {number} amount - Amount to format
   * @returns {string} Formatted currency
   */
  formatCurrency(amount) {
    return convertPrice(amount);
  },
};
